import type { ReceiverState, AcousticConfig } from '../../core';
import { Header } from './Header';
import { SenderView } from './SenderView';
import { ReceiverView } from './ReceiverView';
import { SoftKey } from '../components/SoftKey';
import { SettingsDrawer } from '../components/SettingsDrawer';

type Tab = 'tx' | 'rx';

export class App {
  readonly el: HTMLElement;
  private header: Header;
  private senderView: SenderView;
  private receiverView: ReceiverView;
  private settings: SettingsDrawer;
  private debugLines: string[] = [];
  private debugLogEl: HTMLDivElement;
  private debugDrawerEl: HTMLDivElement;
  private tabBar: HTMLDivElement;
  private mainEl: HTMLElement;
  private currentTab: Tab = 'tx';

  constructor(root: HTMLElement) {
    this.el = root;
    this.el.classList.add('app');
    this.el.innerHTML = '';

    this.settings = new SettingsDrawer();

    this.header = new Header();
    this.header.onMenu(() => this.settings.toggle());
    this.el.appendChild(this.header.el);

    this.tabBar = this.buildTabBar();
    this.el.appendChild(this.tabBar);

    this.mainEl = document.createElement('main');
    this.mainEl.className = 'app-main';

    const cfgGetter = (): Partial<AcousticConfig> => this.settings.readConfig();

    this.senderView = new SenderView(
      cfgGetter,
      (msg) => this.appendLog(msg),
    );
    this.receiverView = new ReceiverView(
      cfgGetter,
      (msg) => this.appendLog(msg),
      (state) => this.handleReceiverState(state),
    );

    this.settings.onChange(() => {
      this.senderView.refreshMeta();
      this.receiverView.applyConfig();
      this.refreshHeader();
    });

    this.mainEl.appendChild(this.senderView.el);
    this.mainEl.appendChild(this.receiverView.el);
    this.el.appendChild(this.mainEl);

    this.debugDrawerEl = this.buildDebugDrawer();
    this.debugLogEl = this.debugDrawerEl.querySelector<HTMLDivElement>('.debug-drawer__log')!;
    this.el.appendChild(this.debugDrawerEl);

    // Settings drawer + backdrop mount last so they sit above the rest.
    this.el.appendChild(this.settings.backdrop);
    this.el.appendChild(this.settings.el);

    this.applyTab('tx');
    this.refreshHeader();
    this.tickHeaderClock();
  }

  private buildTabBar(): HTMLDivElement {
    const bar = document.createElement('div');
    bar.className = 'tab-bar';
    bar.innerHTML = `
      <button class="tab-bar__tab is-active" data-tab="tx" type="button">
        <span class="tab-bar__pip"></span>TX
      </button>
      <button class="tab-bar__tab" data-tab="rx" type="button">
        <span class="tab-bar__pip"></span>RX
      </button>
    `;
    bar.querySelectorAll<HTMLButtonElement>('.tab-bar__tab').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.applyTab(btn.dataset.tab as Tab);
      });
    });
    return bar;
  }

  private applyTab(tab: Tab): void {
    this.currentTab = tab;
    this.tabBar.querySelectorAll<HTMLButtonElement>('.tab-bar__tab').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.tab === tab);
    });
    this.senderView.el.classList.toggle('is-hidden', tab !== 'tx');
    this.receiverView.el.classList.toggle('is-hidden', tab !== 'rx');
  }

  private buildDebugDrawer(): HTMLDivElement {
    const drawer = document.createElement('div');
    drawer.className = 'debug-drawer';

    const head = document.createElement('div');
    head.className = 'debug-drawer__head';
    const title = document.createElement('span');
    title.className = 'debug-drawer__title';
    title.textContent = 'Debug Log';
    const spacer = document.createElement('span');
    spacer.className = 'debug-drawer__spacer';
    const copy = new SoftKey('Copy', 'ghost');
    copy.el.style.minHeight = '28px';
    copy.el.style.padding = '0 12px';
    const toggle = new SoftKey('Show', 'ghost');
    toggle.el.style.minHeight = '28px';
    toggle.el.style.padding = '0 12px';

    head.appendChild(title);
    head.appendChild(spacer);
    head.appendChild(copy.el);
    head.appendChild(toggle.el);

    const log = document.createElement('div');
    log.className = 'debug-drawer__log';

    drawer.appendChild(head);
    drawer.appendChild(log);

    toggle.onClick(() => {
      const open = drawer.classList.toggle('is-open');
      toggle.setLabel(open ? 'Hide' : 'Show');
      if (open) {
        log.textContent = this.debugLines.join('\n');
        log.scrollTop = log.scrollHeight;
      }
    });

    copy.onClick(() => {
      const text = this.debugLines.join('\n');
      void navigator.clipboard?.writeText(text);
      copy.setLabel('Copied');
      window.setTimeout(() => copy.setLabel('Copy'), 1200);
    });

    return drawer;
  }

  private appendLog(msg: string): void {
    const stamp = new Date().toISOString().slice(11, 23);
    this.debugLines.push(`${stamp}  ${msg}`);
    if (this.debugLines.length > 400) this.debugLines.shift();
    if (this.debugDrawerEl.classList.contains('is-open')) {
      this.debugLogEl.textContent = this.debugLines.join('\n');
      this.debugLogEl.scrollTop = this.debugLogEl.scrollHeight;
    }
  }

  private handleReceiverState(state: ReceiverState): void {
    if (state === 'idle') {
      this.header.setStatus('Ready', 'on', false);
      this.header.readouts.mode.setValue('IDLE');
      this.header.readouts.mode.setTone('default');
    } else if (state === 'calibrating') {
      this.header.setStatus('Calibrating', 'warn', true);
      this.header.readouts.mode.setValue('CAL');
      this.header.readouts.mode.setTone('warn');
    } else if (state === 'listening') {
      this.header.setStatus('Listening', 'on', true);
      this.header.readouts.mode.setValue('RX');
      this.header.readouts.mode.setTone('ok');
      this.refreshHeader();
    } else if (state === 'preamble' || state === 'sync') {
      this.header.setStatus('Sync', 'warn', true);
      this.header.readouts.mode.setValue('SYNC');
      this.header.readouts.mode.setTone('warn');
    } else if (state === 'receiving') {
      this.header.setStatus('Receiving', 'on', true);
      this.header.readouts.mode.setValue('RX');
      this.header.readouts.mode.setTone('accent');
    } else if (state === 'complete') {
      this.header.setStatus('Frame OK', 'on', false);
      this.header.readouts.mode.setValue('OK');
      this.header.readouts.mode.setTone('ok');
    } else if (state === 'error') {
      this.header.setStatus('Frame error', 'error', false);
      this.header.readouts.mode.setValue('ERR');
      this.header.readouts.mode.setTone('error');
    }
  }

  private refreshHeader(): void {
    const senderCfg = this.senderView.readConfig();
    const sr = this.receiverView.receiver.audioCtx?.sampleRate ?? 48000;
    this.header.readouts.sampleRate.setValue(formatSampleRate(sr), 'Hz');
    this.header.readouts.baud.setValue(
      String(senderCfg.baudRate ?? 20),
      'sym/s',
    );
    void this.currentTab;
  }

  private tickHeaderClock(): void {
    window.setInterval(() => this.refreshHeader(), 1000);
  }
}

/**
 * Compact sample-rate readout: "48000" → "48 k", "44100" → "44.1 k", etc.
 * Returns whole-k with no decimal when exact, otherwise one decimal place.
 */
function formatSampleRate(hz: number): string {
  if (!Number.isFinite(hz) || hz <= 0) return '—';
  const k = hz / 1000;
  if (Number.isInteger(k)) return `${k} k`;
  return `${k.toFixed(1)} k`;
}
